import Select from 'react-select';
import { useTranslation } from 'react-i18next';

/**
 * Composant Select avec recherche avancée, styling Bootstrap et traductions
 * @param {Object} props - Propriétés du composant
 */
const SearchableSelect = ({
  options = [],
  value,
  onChange,
  labelKey = "label",
  valueKey = "value",
  searchKeys = [],
  placeholder,
  noOptionsMessage,
  isDisabled = false,
  isLoading = false,
  isClearable = true,
  className = "",
  label = "",
  error = "",
  required = false,
  helpText = "",
  ...rest
}) => {
  const { t } = useTranslation(['common', 'messages', 'status']);
  
  // Formater les options pour react-select
  const formattedOptions = Array.isArray(options) 
    ? options.map(option => ({
        value: option[valueKey],
        label: typeof labelKey === "function" ? labelKey(option) : option[labelKey],
        original: option,
      }))
    : [];

  // Trouver la valeur sélectionnée
  const selectedValue = formattedOptions.find(opt => {
    if (!value) return false;
    return opt.value === (value[valueKey] || value);
  });

  /**
   * Gérer le changement de sélection
   * @param {Object} selected - Option sélectionnée
   */
  const handleChange = (selected) => {
    onChange(selected ? selected.original : null);
  };

  /**
   * Filtre personnalisé pour la recherche
   * @param {Object} option - Option à filtrer
   * @param {string} searchText - Texte de recherche
   * @returns {boolean} - True si l'option correspond
   */
  const customFilterOption = (option, searchText) => {
    if (!searchText) return true;
    
    const lowerSearch = searchText.toLowerCase();
    
    // Rechercher dans les clés spécifiées
    if (searchKeys.length > 0) {
      return searchKeys.some(key => {
        const value = option.data.original[key];
        return value && value.toString().toLowerCase().includes(lowerSearch);
      });
    }
    
    // Rechercher par défaut dans le label
    return option.label.toLowerCase().includes(lowerSearch);
  };

  // Messages de traduction
  const placeholderText = placeholder || t('common.select');
  const noOptionsMessageText = noOptionsMessage || t('messages.noResults');
  const loadingMessageText = t('status.loading');

  // Styles personnalisés compatibles Bootstrap
  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '0.375rem',
      borderColor: error ? '#dc3545' : state.isFocused ? '#0d6efd' : '#dee2e6',
      boxShadow: state.isFocused 
        ? `0 0 0 0.25rem ${error ? 'rgba(220, 53, 69, 0.25)' : 'rgba(13, 110, 253, 0.25)'}`
        : 'none',
      '&:hover': {
        borderColor: error ? '#dc3545' : '#86b7fe',
      },
      minHeight: 'calc(1.5em + 1rem + 2px)',
      backgroundColor: isDisabled ? '#e9ecef' : '#fff',
      cursor: isDisabled ? 'not-allowed' : 'default',
      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6c757d',
      fontSize: '1rem',
    }),
    input: (base) => ({
      ...base,
      color: '#212529',
      fontSize: '1rem',
      margin: 0,
      padding: 0,
    }),
    singleValue: (base) => ({
      ...base,
      color: '#212529',
      fontSize: '1rem',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#182848' 
        : state.isFocused 
        ? '#e7f1ff' 
        : '#fff',
      color: state.isSelected ? '#fff' : '#212529',
      cursor: 'pointer',
      fontSize: '1rem',
      padding: '0.5rem 1rem',
      borderRadius: '5px',
      '&:active': {
        backgroundColor: state.isSelected ? '#0a58ca' : '#e7f1ff',
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '0.375rem',
      boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
      border: '1px solid #dee2e6',
      zIndex: 1050,
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
      maxHeight: '250px',
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: '#dee2e6',
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? '#0d6efd' : '#6c757d',
      transition: 'color 0.15s ease-in-out',
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#182848',
      '&:hover': {
        color: '#dc3545',
      },
    }),
  };

  return (
    <div className={`mb-3 ${className}`}>
      {label && (
        <label className="form-label fw-semibold">
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      
      <Select
        options={formattedOptions}
        value={selectedValue}
        onChange={handleChange}
        filterOption={customFilterOption}
        isSearchable
        isDisabled={isDisabled}
        isLoading={isLoading}
        isClearable={isClearable}
        placeholder={placeholderText}
        noOptionsMessage={() => noOptionsMessageText}
        loadingMessage={() => loadingMessageText}
        styles={customStyles}
        classNamePrefix="react-select"
        {...rest}
      />
      
      {error && (
        <div className="invalid-feedback d-block">{error}</div>
      )}
      
      {helpText && !error && (
        <div className="form-text text-muted">{helpText}</div>
      )}
    </div>
  );
};

export default SearchableSelect;